// Launch answer kinds (contracts/launch-types.md §1): canonical strings, the player's progress, keypad keys.
// Pure and React-free, so digest.ts (and Node servers through the `digest` subpath) can use it.
//
//   number   {kind:"number",   value:"42"}            -> "42"  (no leading zeros, "-" for negatives)
//   digits   {kind:"digits",   value:"0385291"}       -> "0385291"
//   fill     {kind:"fill",     value:[3,1,4]}         -> "3,1,4" (answer.cells order)
//   sequence {kind:"sequence", value:[[0,1],[2,3]]}   -> "0,1;2,3" (TAP ORDER, never sorted)
//   rows     {kind:"rows",     value:"YNNY"}          -> "YNNY"
//
// Mirrors content/src/iqcontent/canonical.py and backend/api/src/domain/canonical.ts. While a player is
// still typing, a selection can be partial (fill `null` blanks, rows `_`, fewer digits); `answerProgress`
// says whether it is complete, and the canonical helpers throw on anything incomplete or malformed.

import type {
  Answer,
  AnswerSpec,
  CellRef,
  DigitsAnswerSpec,
  FillAnswerSpec,
  NumberAnswerSpec,
  RowsAnswerSpec,
  Selection,
  SequenceAnswerSpec,
} from "../types";
import { kindOf } from "../types";

export const LAUNCH_ANSWER_KINDS = ["number", "digits", "fill", "sequence", "rows"] as const;
export type LaunchAnswerKind = (typeof LAUNCH_ANSWER_KINDS)[number];
export type LaunchAnswerSpec = NumberAnswerSpec | DigitsAnswerSpec | FillAnswerSpec | SequenceAnswerSpec | RowsAnswerSpec;

export const isLaunchAnswerSpec = (spec: unknown): spec is LaunchAnswerSpec =>
  (LAUNCH_ANSWER_KINDS as readonly string[]).includes(kindOf(spec));

const INT = /^-?[0-9]+$/;
const DIGITS = /^[0-9]+$/;

/** answer.number@1 canonical: "42", "-7"; "-0" and "007" normalise. Throws on anything else. */
export function canonicalNumber(value: unknown): string {
  if (typeof value === "number" && Number.isInteger(value)) return String(value === 0 ? 0 : value);
  const s = String(value ?? "").trim();
  if (!INT.test(s)) throw new Error(`number answer must be an integer, got ${JSON.stringify(value)}`);
  const negative = s.startsWith("-");
  const digits = s.replace(/^-/, "").replace(/^0+(?=\d)/, "");
  return negative && digits !== "0" ? `-${digits}` : digits;
}

export function canonicalDigits(value: unknown, length?: number): string {
  const s = Array.isArray(value) ? value.map((d) => String(d).trim()).join("") : String(value ?? "").trim();
  if (!DIGITS.test(s)) throw new Error(`digits answer must be digits 0-9, got ${JSON.stringify(value)}`);
  if (length !== undefined && s.length !== length) throw new Error(`digits answer must have ${length} digits, got ${s.length}`);
  return s;
}

export function canonicalFill(values: unknown, count?: number): string {
  const list = Array.isArray(values) ? values : [];
  if (list.length === 0) throw new Error("fill answer has no values");
  if (count !== undefined && list.length !== count) throw new Error(`fill answer must have ${count} values, got ${list.length}`);
  return list.map((v) => canonicalNumber(v)).join(",");
}

export function canonicalSequence(cells: unknown): string {
  const list = Array.isArray(cells) ? cells : [];
  if (list.length === 0) throw new Error("sequence answer has no cells");
  return list
    .map((c) => {
      const r = Math.trunc(Number((c as unknown[])?.[0]));
      const col = Math.trunc(Number((c as unknown[])?.[1]));
      if (!Number.isFinite(r) || !Number.isFinite(col) || r < 0 || col < 0) throw new Error(`bad sequence cell ${JSON.stringify(c)}`);
      return `${r},${col}`;
    })
    .join(";");
}

export function canonicalRows(value: unknown, count?: number): string {
  const s = Array.isArray(value)
    ? value.map((v) => (typeof v === "boolean" ? (v ? "Y" : "N") : String(v).trim().toUpperCase())).join("")
    : String(value ?? "").trim().toUpperCase();
  if (!/^[YN]+$/.test(s)) throw new Error(`rows answer must be Y/N per row, got ${JSON.stringify(value)}`);
  if (count !== undefined && s.length !== count) throw new Error(`rows answer must have ${count} rows, got ${s.length}`);
  return s;
}

/** The canonical string of a launch-kind answer (throws when it is partial or malformed). */
export function canonicalLaunchAnswer(answer: { kind: string; value: unknown }): string {
  switch (answer.kind) {
    case "number":
      return canonicalNumber(answer.value);
    case "digits":
      return canonicalDigits(answer.value);
    case "fill":
      return canonicalFill(answer.value);
    case "sequence":
      return canonicalSequence(answer.value);
    case "rows":
      return canonicalRows(answer.value);
    default:
      throw new Error(`No canonical form for answer kind ${answer.kind}`);
  }
}

// ---------------------------------------------------------------- spec readers (defensive: specs come from the network)

const int = (v: unknown, fallback: number) => (typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : fallback);
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

export const cellKey = (r: number, c: number) => `${r},${c}`;

export function cellList(v: unknown): CellRef[] {
  return (Array.isArray(v) ? v : [])
    .filter((c): c is unknown[] => Array.isArray(c) && c.length >= 2)
    .map((c) => [int(c[0], -1), int(c[1], -1)] as CellRef)
    .filter(([r, c]) => r >= 0 && c >= 0);
}

/** How many digits the keypad takes (not counting "-"): `maxDigits`, else what min/max need, else 6. */
export function numberMaxDigits(spec: NumberAnswerSpec): number {
  if (typeof spec.maxDigits === "number") return clamp(int(spec.maxDigits, 6), 1, 6);
  const bounds = [spec.min, spec.max].filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (bounds.length === 2) return clamp(Math.max(...bounds.map((b) => String(Math.abs(Math.trunc(b))).length)), 1, 6);
  return 6;
}

export const digitsLength = (spec: DigitsAnswerSpec) => clamp(int(spec.length, 4), 1, 12);
export const rowsCount = (spec: RowsAnswerSpec) => clamp(int(spec.count, 1), 1, 12);
export const sequenceCount = (spec: SequenceAnswerSpec) => clamp(int(spec.count, 2), 1, 144);

export function fillRange(spec: FillAnswerSpec): { min: number; max: number } {
  const min = int(spec.min, 0);
  const max = int(spec.max, 9);
  return min <= max ? { min, max } : { min: max, max: min };
}

/** The keys a fill keypad shows: min…max when that's single digits (KenKen 1–4, Kakuro 1–9), else 0–9 (multi-digit entry). */
export function fillKeys(spec: FillAnswerSpec): { keys: number[]; multiDigit: boolean; maxDigits: number; negative: boolean } {
  const { min, max } = fillRange(spec);
  const maxDigits = Math.max(String(Math.abs(min)).length, String(Math.abs(max)).length);
  if (min >= 0 && max <= 9) return { keys: Array.from({ length: max - min + 1 }, (_, i) => min + i), multiDigit: false, maxDigits: 1, negative: false };
  return { keys: [1, 2, 3, 4, 5, 6, 7, 8, 9, 0], multiDigit: true, maxDigits: clamp(maxDigits, 1, 6), negative: min < 0 };
}

/** A fill selection's values, one per blank (null when empty), whatever shape it arrived in. */
export function fillValues(answer: Selection | null | undefined, count: number): (number | null)[] {
  const raw = answer?.kind === "fill" && Array.isArray(answer.value) ? answer.value : [];
  return Array.from({ length: count }, (_, i) => {
    const v = raw[i] as unknown;
    if (v === null || v === undefined || v === "") return null;
    const n = typeof v === "number" ? v : Number(String(v).trim());
    return Number.isInteger(n) ? n : null;
  });
}

/** A rows selection as `count` characters, `Y`, `N` or `_` (unanswered). */
export function rowsValue(answer: Selection | null | undefined, count: number): string {
  const raw = answer?.kind === "rows" ? String(answer.value ?? "").toUpperCase() : "";
  return Array.from({ length: count }, (_, i) => (raw[i] === "Y" || raw[i] === "N" ? raw[i] : "_")).join("");
}

export function numberText(answer: Selection | null | undefined): string {
  if (answer?.kind !== "number") return "";
  const v = answer.value as unknown;
  return typeof v === "number" ? String(v) : String(v ?? "").trim();
}

export function digitsText(answer: Selection | null | undefined): string {
  if (answer?.kind !== "digits") return "";
  const v = answer.value as unknown;
  return (Array.isArray(v) ? v.join("") : String(v ?? "")).replace(/[^0-9]/g, "");
}

export const sequenceCells = (answer: Selection | null | undefined): CellRef[] => (answer?.kind === "sequence" ? cellList(answer.value) : []);

// ---------------------------------------------------------------- progress (what the host's CTA says)

export interface AnswerProgress {
  /** The selection can be submitted as is. */
  complete: boolean;
  /** Steps done and needed ("3 / 8 tapped"); for choice and number, 0 or 1. */
  done: number;
  total: number;
  /** The CTA's text while incomplete (board 14/17/18): "Enter a number", "Fill every blank", "Tap all 8", … */
  hint: string;
}

/**
 * Whether `selection` is a complete answer for `spec.answer`, and the CTA hint while it isn't. Covers every
 * answer kind, so hosts gate their lock-in button on it: the keypad unlocks on the first digit, fill once
 * every blank has a value, digits once every slot is typed, sequence at `count` taps, rows once every row is
 * answered.
 */
export function answerProgress(spec: { answer?: AnswerSpec } | null | undefined, selection: Selection | null | undefined): AnswerProgress {
  const a = spec?.answer;
  switch (kindOf(a)) {
    case "choice": {
      const done = selection?.kind === "choice" ? 1 : 0;
      return { complete: done === 1, done, total: 1, hint: "Pick an option" };
    }
    case "cellSet": {
      const total = Math.max(1, int((a as { count?: unknown }).count, 1));
      const done = selection?.kind === "cellSet" && Array.isArray(selection.value) ? selection.value.length : 0;
      return { complete: done === total, done, total, hint: `Tap ${Math.max(0, total - done)} more` };
    }
    case "number": {
      const text = numberText(selection);
      const done = /\d/.test(text) && INT.test(text) ? 1 : 0;
      return { complete: done === 1, done, total: 1, hint: "Enter a number" };
    }
    case "digits": {
      const total = digitsLength(a as DigitsAnswerSpec);
      const done = Math.min(total, digitsText(selection).length);
      return { complete: done === total, done, total, hint: `Enter all ${total}` };
    }
    case "fill": {
      const total = cellList((a as FillAnswerSpec).cells).length;
      const done = fillValues(selection, total).filter((v) => v !== null).length;
      return { complete: total > 0 && done === total, done, total, hint: "Fill every blank" };
    }
    case "sequence": {
      const total = sequenceCount(a as SequenceAnswerSpec);
      const done = Math.min(total, sequenceCells(selection).length);
      return { complete: done === total, done, total, hint: `Tap all ${total}` };
    }
    case "rows": {
      const total = rowsCount(a as RowsAnswerSpec);
      const done = [...rowsValue(selection, total)].filter((ch) => ch !== "_").length;
      return { complete: done === total, done, total, hint: "Answer every row" };
    }
    default:
      return { complete: false, done: 0, total: 1, hint: "Pick an answer" };
  }
}

/** `answerProgress(...).complete`. */
export const isAnswerComplete = (spec: { answer?: AnswerSpec } | null | undefined, selection: Selection | null | undefined): selection is Answer =>
  answerProgress(spec, selection).complete;
